import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { getImageUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FavoriteButton from "@/components/FavoriteButton";
import RadarChart from "@/components/charts/RadarChart";
import BarChart from "@/components/charts/BarChart";
import {
  Heart, Users, GitCompare, BookmarkPlus, Bookmark, Crown, Lock, MapPin, Briefcase,
  Download, X, Trash2, Trophy, Zap, Activity, Plus, Search, ShieldCheck, BarChart3, Info
} from "lucide-react";
import { toast } from "sonner";

const ERGO_FIELDS = [
  { key: "sprint_speed", label: "Sprint", unit: "km/h", lowerBetter: false, icon: Zap, color: "#F85037" },
  { key: "vo2_max", label: "VO2 Max", unit: "ml/kg/min", lowerBetter: false, icon: Activity, color: "#074572" },
  { key: "vertical_jump", label: "Vert Jump", unit: "cm", lowerBetter: false, icon: Trophy, color: "#10B981" },
  { key: "agility_time", label: "Agility", unit: "sec", lowerBetter: true, icon: Activity, color: "#F59E0B" },
  { key: "bench_press", label: "Bench", unit: "kg", lowerBetter: false, icon: Activity, color: "#8B5CF6" },
  { key: "endurance_score", label: "Endurance", unit: "/100", lowerBetter: false, icon: Activity, color: "#06B6D4" },
];

const SCOUT_LIKE_ROLES = ["scout", "coach", "club", "admin", "super_admin", "moderator", "verification_admin"];

const TAB_INFO = {
  shortlist: "Athletes you've favourited. Select two or more to run a Compare and see how they stack up.",
  compare: "Side-by-side profile & ergometrics for up to 8 athletes. Percentiles unlock with Premium.",
  saved: "Save any Scout Navigator filter combination and re-run it in one click. Premium feature.",
  graphs: "Radar overlay + per-metric bars for your selected athletes vs peer position average. Premium.",
  export: "Download bio + ergometrics of your shortlist (or selection) as CSV. Premium.",
};

function InfoTooltip({ text, testId }) {
  return (
    <span
      role="button"
      tabIndex={0}
      title={text}
      className="inline-flex items-center text-zinc-400 hover:text-[#074572] cursor-help ml-0.5"
      data-testid={testId}
    >
      <Info size={12} />
    </span>
  );
}

export default function ScoutDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "shortlist";
  const isPremium = !!user?.is_premium;
  const allowed = SCOUT_LIKE_ROLES.includes(user?.role);

  if (!allowed) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="dashboard-locked">
        <Lock size={48} className="mx-auto text-zinc-300 mb-4" />
        <h1 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Scout & Coach Dashboard</h1>
        <p className="text-sm text-zinc-500 mt-2">This area is reserved for scouts, coaches and clubs. Switch your account role to access it.</p>
        <Button className="mt-6 bg-[#074572] text-white" onClick={() => navigate("/feed")}>Go to Feed</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="scout-dashboard">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-medium text-zinc-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            Scout Dashboard
            {isPremium && <Crown size={18} className="text-amber-500" />}
          </h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            Shortlist, compare, and analyse talent in one place.
            {!isPremium && <span className="ml-1.5 text-[#F85037] font-medium">Upgrade for 8-way compare, percentiles, saved searches, graphs & CSV export.</span>}
          </p>
        </div>
        {!isPremium && (
          <Link to="/premium">
            <Button className="bg-[#F85037] hover:bg-[#e04530] text-white gap-1.5" data-testid="dashboard-upgrade-btn">
              <Crown size={14} /> Upgrade to Premium
            </Button>
          </Link>
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
        <TabsList className="bg-transparent border-b border-[#E6E6E6] rounded-none w-full justify-start gap-6 px-0 h-auto pb-0">
          <TabsTrigger value="shortlist" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-1.5" data-testid="dashboard-tab-shortlist">
            <Heart size={14} /> Shortlist
            <InfoTooltip text={TAB_INFO.shortlist} testId="info-shortlist" />
          </TabsTrigger>
          <TabsTrigger value="compare" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-1.5" data-testid="dashboard-tab-compare">
            <GitCompare size={14} /> Compare
            <InfoTooltip text={TAB_INFO.compare} testId="info-compare" />
          </TabsTrigger>
          <TabsTrigger value="saved" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-1.5" data-testid="dashboard-tab-saved">
            <Bookmark size={14} /> Saved Searches
            {!isPremium && <Crown size={10} className="text-amber-500" />}
            <InfoTooltip text={TAB_INFO.saved} testId="info-saved" />
          </TabsTrigger>
          <TabsTrigger value="graphs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-1.5" data-testid="dashboard-tab-graphs">
            <BarChart3 size={14} /> Graphs
            {!isPremium && <Crown size={10} className="text-amber-500" />}
            <InfoTooltip text={TAB_INFO.graphs} testId="info-graphs" />
          </TabsTrigger>
          <TabsTrigger value="export" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-1.5" data-testid="dashboard-tab-export">
            <Download size={14} /> Export
            {!isPremium && <Crown size={10} className="text-amber-500" />}
            <InfoTooltip text={TAB_INFO.export} testId="info-export" />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="shortlist" className="mt-6">
          <ShortlistSection isPremium={isPremium} />
        </TabsContent>
        <TabsContent value="compare" className="mt-6">
          <CompareSection isPremium={isPremium} />
        </TabsContent>
        <TabsContent value="saved" className="mt-6">
          <SavedSearchesSection isPremium={isPremium} />
        </TabsContent>
        <TabsContent value="graphs" className="mt-6">
          <GraphsSection isPremium={isPremium} />
        </TabsContent>
        <TabsContent value="export" className="mt-6">
          <ExportSection isPremium={isPremium} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ============ SHORTLIST ============ */
function ShortlistSection({ isPremium }) {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/favorites");
      setAthletes(data.athletes || []);
    } catch { toast.error("Failed to load shortlist"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const goCompare = () => {
    if (selected.size < 2) { toast.error("Select at least 2 athletes to compare"); return; }
    const max = isPremium ? 8 : 2;
    if (selected.size > max) { toast.error(`Free tier limited to ${max}. Upgrade to compare more.`); return; }
    navigate(`/dashboard?tab=compare&ids=${[...selected].join(",")}`);
  };

  if (loading) return <p className="text-center py-12 text-zinc-400 text-sm">Loading shortlist...</p>;
  if (athletes.length === 0) return (
    <div className="text-center py-16" data-testid="shortlist-empty">
      <Heart size={48} className="mx-auto text-zinc-200 mb-4" />
      <p className="text-zinc-500">No athletes in your shortlist yet</p>
      <p className="text-sm text-zinc-400 mt-1">Tap the heart icon on any athlete card to save them here.</p>
      <Link to="/scout"><Button className="mt-4 bg-[#074572] hover:bg-[#053a60] text-white">Browse Scout Navigator</Button></Link>
    </div>
  );

  return (
    <div data-testid="shortlist-list">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-zinc-500">{athletes.length} athlete{athletes.length === 1 ? "" : "s"} · {selected.size} selected</p>
        <Button onClick={goCompare} disabled={selected.size < 2} className="bg-[#074572] hover:bg-[#053a60] text-white gap-1.5 h-8 text-xs" data-testid="shortlist-compare-btn">
          <GitCompare size={12} /> Compare Selected ({selected.size})
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {athletes.map(a => {
          const initials = a.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
          const avatar = getImageUrl(a.profile_image);
          const isSel = selected.has(a._id);
          return (
            <div key={a._id} className={`bg-white border rounded-lg p-3 flex items-center gap-3 transition-all ${isSel ? "border-[#074572] ring-1 ring-[#074572]/20" : "border-[#E6E6E6]"}`} data-testid={`shortlist-item-${a._id}`}>
              <input type="checkbox" checked={isSel} onChange={() => toggleSelect(a._id)} className="w-4 h-4 accent-[#074572] shrink-0" data-testid={`shortlist-select-${a._id}`} />
              <Link to={`/profile/${a._id}`} className="flex items-center gap-3 flex-1 min-w-0">
                <Avatar className="h-11 w-11 shrink-0">
                  {avatar ? <AvatarImage src={avatar} alt={a.name} /> : null}
                  <AvatarFallback className="bg-[#074572] text-white text-xs font-medium">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-semibold text-zinc-900 truncate">{a.name}</p>
                    {a.ergo_verified && <ShieldCheck size={11} className="text-[#074572]" />}
                  </div>
                  {a.position && <p className="text-xs text-zinc-500">{a.position}</p>}
                  <div className="flex items-center gap-2 text-[11px] text-zinc-400 mt-0.5">
                    {a.location && <span className="flex items-center gap-0.5 truncate"><MapPin size={9} />{a.location}</span>}
                    {a.club && <span className="flex items-center gap-0.5 truncate"><Briefcase size={9} />{a.club}</span>}
                  </div>
                </div>
              </Link>
              <FavoriteButton athleteId={a._id} size={16} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============ COMPARE ============ */
function CompareSection({ isPremium }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const idsParam = searchParams.get("ids") || "";
  const [ids, setIds] = useState(idsParam ? idsParam.split(",") : []);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [percentiles, setPercentiles] = useState({});
  const maxAllowed = isPremium ? 8 : 2;

  useEffect(() => { setIds(idsParam ? idsParam.split(",") : []); }, [idsParam]);

  useEffect(() => {
    if (ids.length === 0) { setAthletes([]); return; }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/scout/compare?ids=${ids.join(",")}`);
        if (active) setAthletes(data.athletes || []);
      } catch (err) {
        if (active) toast.error(err.response?.data?.detail || "Compare failed");
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [ids]);

  // Percentiles (premium-only)
  useEffect(() => {
    if (!isPremium || athletes.length === 0) return;
    let active = true;
    (async () => {
      const map = {};
      await Promise.all(athletes.map(async (a) => {
        try {
          const { data } = await api.get(`/scout/percentiles/${a._id}`);
          map[a._id] = data.percentiles || {};
        } catch { /* ignore - athlete may not have ergo data */ }
      }));
      if (active) setPercentiles(map);
    })();
    return () => { active = false; };
  }, [athletes, isPremium]);

  const removeAthlete = (id) => {
    const next = ids.filter(i => i !== id);
    setSearchParams(next.length ? { tab: "compare", ids: next.join(",") } : { tab: "compare" });
  };
  const addAthlete = (athlete) => {
    if (ids.includes(athlete._id)) return;
    if (ids.length >= maxAllowed) {
      toast.error(`Limit reached (${maxAllowed}). ${isPremium ? "Max 8 athletes." : "Upgrade for more."}`);
      return;
    }
    const next = [...ids, athlete._id];
    setSearchParams({ tab: "compare", ids: next.join(",") });
  };
  const addAthletes = (chosen) => {
    if (chosen.length === 0) { setPickerOpen(false); return; }
    const available = Math.max(0, maxAllowed - ids.length);
    if (available <= 0) {
      toast.error(`Limit reached (${maxAllowed}). ${isPremium ? "Max 8 athletes." : "Upgrade for more."}`);
      setPickerOpen(false);
      return;
    }
    const toAdd = chosen.filter(c => !ids.includes(c._id)).slice(0, available);
    if (toAdd.length === 0) { setPickerOpen(false); return; }
    const next = [...ids, ...toAdd.map(a => a._id)];
    setSearchParams({ tab: "compare", ids: next.join(",") });
    setPickerOpen(false);
    if (chosen.length > toAdd.length) {
      toast.info(`Added ${toAdd.length} — limit reached at ${maxAllowed}`);
    } else {
      toast.success(`Added ${toAdd.length} athlete${toAdd.length === 1 ? "" : "s"} to comparison`);
    }
  };

  // Find best/worst per field
  const fieldExtremes = {};
  ERGO_FIELDS.forEach(f => {
    const values = athletes.map(a => a[f.key]).filter(v => v != null);
    if (values.length < 2) return;
    fieldExtremes[f.key] = {
      best: f.lowerBetter ? Math.min(...values) : Math.max(...values),
      worst: f.lowerBetter ? Math.max(...values) : Math.min(...values),
    };
  });

  return (
    <div data-testid="compare-section">
      <div className="bg-white border border-[#E6E6E6] rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <p className="text-xs text-zinc-500">{athletes.length}/{maxAllowed} athletes · {isPremium ? "Premium" : "Free"} tier</p>
          <Button onClick={() => setPickerOpen(true)} disabled={ids.length >= maxAllowed} className="bg-[#074572] hover:bg-[#053a60] text-white gap-1.5 h-8 text-xs" data-testid="compare-add-btn">
            <Plus size={12} /> Add Athlete
          </Button>
        </div>
        {!isPremium && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 flex items-center gap-1.5">
            <Crown size={11} className="text-amber-500" />
            Free tier limited to 2 athletes. Upgrade for up to 8-way comparison + ergometric percentiles.
          </div>
        )}
      </div>

      {loading ? <p className="text-center py-12 text-zinc-400 text-sm">Loading comparison...</p>
      : athletes.length === 0 ? (
        <div className="text-center py-16" data-testid="compare-empty">
          <GitCompare size={48} className="mx-auto text-zinc-200 mb-4" />
          <p className="text-zinc-500">Add at least 2 athletes to start comparing</p>
          <Button onClick={() => setPickerOpen(true)} className="mt-4 bg-[#074572] hover:bg-[#053a60] text-white">Add Athletes</Button>
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#E6E6E6] rounded-lg bg-white" data-testid="compare-table">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-[#E6E6E6] bg-zinc-50/50">
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-32">Metric</th>
                {athletes.map(a => {
                  const initials = a.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
                  const avatar = getImageUrl(a.profile_image);
                  return (
                    <th key={a._id} className="p-3 text-center min-w-[140px]">
                      <div className="flex flex-col items-center gap-1 relative">
                        <button onClick={() => removeAthlete(a._id)} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-zinc-100 hover:bg-[#F85037] hover:text-white flex items-center justify-center text-zinc-500 transition-colors" data-testid={`compare-remove-${a._id}`}>
                          <X size={11} />
                        </button>
                        <Link to={`/profile/${a._id}`}>
                          <Avatar className="h-12 w-12 mb-1">
                            {avatar ? <AvatarImage src={avatar} alt={a.name} /> : null}
                            <AvatarFallback className="bg-[#074572] text-white text-xs">{initials}</AvatarFallback>
                          </Avatar>
                        </Link>
                        <Link to={`/profile/${a._id}`} className="text-xs font-semibold text-zinc-900 hover:text-[#074572] truncate max-w-[140px]">{a.name}</Link>
                        <Badge className="text-[9px] bg-[#074572]/5 text-[#074572] capitalize">{a.position || a.role}</Badge>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <CompareRow label="Age" athletes={athletes} valueFn={a => a.age} />
              <CompareRow label="Location" athletes={athletes} valueFn={a => a.location} />
              <CompareRow label="Club" athletes={athletes} valueFn={a => a.club} />
              <CompareRow label="Foot" athletes={athletes} valueFn={a => a.preferred_foot} />
              <CompareRow label="Height" athletes={athletes} valueFn={a => a.height} />
              <CompareRow label="Weight" athletes={athletes} valueFn={a => a.weight} />
              <CompareRow label="Experience" athletes={athletes} valueFn={a => a.experience_years ? `${a.experience_years}yr` : "-"} />
              <tr className="bg-zinc-50/30">
                <td colSpan={athletes.length + 1} className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#F85037] font-bold">Ergometrics</td>
              </tr>
              {ERGO_FIELDS.map(f => (
                <CompareRow
                  key={f.key}
                  label={`${f.label} (${f.unit})`}
                  athletes={athletes}
                  valueFn={a => a[f.key]}
                  extremes={fieldExtremes[f.key]}
                  percentiles={isPremium ? percentiles : null}
                  fieldKey={f.key}
                  lowerBetter={f.lowerBetter}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AthletePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={addAthletes}
        excludeIds={ids}
        maxToAdd={Math.max(0, maxAllowed - ids.length)}
      />
    </div>
  );
}

function CompareRow({ label, athletes, valueFn, extremes, percentiles, fieldKey, lowerBetter }) {
  return (
    <tr className="border-b border-zinc-100 last:border-0">
      <td className="px-3 py-2 text-xs text-zinc-500 font-medium">{label}</td>
      {athletes.map(a => {
        const value = valueFn(a);
        const isBest = extremes && value === extremes.best && value !== extremes.worst;
        const pct = percentiles && fieldKey ? percentiles[a._id]?.[fieldKey] : null;
        return (
          <td key={a._id} className={`px-3 py-2 text-center text-sm ${isBest ? "font-bold text-[#074572]" : "text-zinc-700"}`}>
            <div className="flex flex-col items-center gap-0.5">
              <span>{value != null && value !== "" ? value : <span className="text-zinc-300">-</span>}</span>
              {pct != null && <span className="text-[9px] text-zinc-400">{pct}th pct</span>}
              {isBest && <span className="text-[9px] text-[#10B981] font-semibold uppercase">{lowerBetter ? "fastest" : "best"}</span>}
            </div>
          </td>
        );
      })}
    </tr>
  );
}

function AthletePicker({ open, onClose, onConfirm, excludeIds = [], maxToAdd = 8 }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState([]);

  useEffect(() => { if (!open) { setQuery(""); setResults([]); setChosen([]); } }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        params.set("role", "athlete");
        if (query) params.set("search", query);
        params.set("limit", 20);
        const { data } = await api.get(`/scout/discover?${params}`);
        if (active) setResults((data.users || []).filter(u => !excludeIds.includes(u._id)));
      } catch { /* ignore */ }
      finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [query, open, excludeIds]);

  const toggle = (athlete) => {
    setChosen(prev => {
      const exists = prev.find(a => a._id === athlete._id);
      if (exists) return prev.filter(a => a._id !== athlete._id);
      if (prev.length >= maxToAdd) {
        toast.error(`You can add up to ${maxToAdd} more`);
        return prev;
      }
      return [...prev, athlete];
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>Add Athletes to Compare</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 -mt-2">Pick multiple athletes, then tap Add. You can add up to {maxToAdd} more.</p>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search athletes by name, position..." className="pl-9 border-[#E6E6E6]" data-testid="compare-picker-search" autoFocus />
        </div>
        <div className="max-h-72 overflow-y-auto -mx-1 mt-1">
          {loading && results.length === 0 ? <p className="text-center py-4 text-xs text-zinc-400">Loading...</p>
          : results.length === 0 ? <p className="text-center py-4 text-xs text-zinc-400">No athletes found</p>
          : results.map(a => {
            const initials = a.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
            const isChosen = chosen.some(c => c._id === a._id);
            return (
              <button
                key={a._id}
                type="button"
                onClick={() => toggle(a)}
                className={`w-full text-left px-3 py-2 hover:bg-zinc-50 rounded-md flex items-center gap-2 ${isChosen ? "bg-[#074572]/5" : ""}`}
                data-testid={`compare-picker-${a._id}`}
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={isChosen}
                  className="w-4 h-4 accent-[#074572] shrink-0"
                  data-testid={`compare-picker-check-${a._id}`}
                />
                <Avatar className="h-8 w-8 shrink-0"><AvatarFallback className="bg-[#074572] text-white text-[10px]">{initials}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-900 truncate">{a.name}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{a.position || "Athlete"} · {a.location || "—"}</p>
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-[#E6E6E6] pt-3 -mx-1">
          <span className="text-xs text-zinc-500 px-1" data-testid="compare-picker-count">
            {chosen.length} selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="border-[#E6E6E6] h-8 text-xs" data-testid="compare-picker-cancel">Cancel</Button>
            <Button
              onClick={() => onConfirm(chosen)}
              disabled={chosen.length === 0}
              className="bg-[#074572] hover:bg-[#053a60] text-white h-8 text-xs"
              data-testid="compare-picker-confirm"
            >
              Add {chosen.length > 0 ? `(${chosen.length})` : ""}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============ SAVED SEARCHES ============ */
function SavedSearchesSection({ isPremium }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/saved-searches");
      setItems(data.items || []);
    } catch (err) { if (err.response?.status !== 403) toast.error("Failed to load"); }
    setLoading(false);
  }, []);

  useEffect(() => { if (isPremium) load(); else setLoading(false); }, [isPremium, load]);

  if (!isPremium) return <PremiumLock title="Saved Searches are Premium" description="Save your favourite filter combinations and re-run them with one click." />;

  const runSearch = (item) => {
    const params = new URLSearchParams();
    Object.entries(item.filters || {}).forEach(([k, v]) => { if (v) params.set(k, v); });
    navigate(`/scout?${params.toString()}`);
  };

  const del = async (id) => {
    try { await api.delete(`/saved-searches/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="saved-searches">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-zinc-500">{items.length} saved search{items.length === 1 ? "" : "es"}</p>
        <Button onClick={() => setModalOpen(true)} className="bg-[#074572] hover:bg-[#053a60] text-white gap-1.5 h-8 text-xs" data-testid="open-save-search">
          <BookmarkPlus size={12} /> New Saved Search
        </Button>
      </div>
      {loading ? <p className="text-center py-12 text-zinc-400 text-sm">Loading...</p>
      : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E6E6E6] rounded-lg">
          <Bookmark size={40} className="mx-auto text-zinc-200 mb-3" />
          <p className="text-sm text-zinc-500">No saved searches yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(s => (
            <div key={s.id} className="bg-white border border-[#E6E6E6] rounded-md p-3 flex items-center gap-3" data-testid={`saved-row-${s.id}`}>
              <Bookmark size={16} className="text-[#074572] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900 truncate">{s.name}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5 truncate">
                  {Object.entries(s.filters || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ") || "No filters"}
                </p>
              </div>
              <Button size="sm" variant="outline" className="text-xs h-7 border-[#E6E6E6] text-[#074572]" onClick={() => runSearch(s)} data-testid={`saved-run-${s.id}`}>Run</Button>
              <button onClick={() => del(s.id)} className="text-zinc-400 hover:text-[#F85037] p-1" data-testid={`saved-delete-${s.id}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
      <SaveSearchModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />
    </div>
  );
}

function SaveSearchModal({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [filters, setFilters] = useState({ role: "athlete", position: "", location: "", min_age: "", max_age: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) { setName(""); setFilters({ role: "athlete", position: "", location: "", min_age: "", max_age: "" }); } }, [open]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const cleaned = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      await api.post("/saved-searches", { name: name.trim(), filters: cleaned });
      toast.success("Saved!");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>New Saved Search</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-zinc-600">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. U21 Midfielders in Spain" className="border-[#E6E6E6] mt-1" data-testid="save-search-name" autoFocus /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-zinc-600">Position</Label><Input value={filters.position} onChange={(e) => setFilters({...filters, position: e.target.value})} placeholder="e.g. Midfielder" className="border-[#E6E6E6] mt-1" data-testid="save-search-position" /></div>
            <div><Label className="text-xs text-zinc-600">Location</Label><Input value={filters.location} onChange={(e) => setFilters({...filters, location: e.target.value})} placeholder="e.g. Spain" className="border-[#E6E6E6] mt-1" data-testid="save-search-location" /></div>
            <div><Label className="text-xs text-zinc-600">Min Age</Label><Input type="number" value={filters.min_age} onChange={(e) => setFilters({...filters, min_age: e.target.value})} className="border-[#E6E6E6] mt-1" data-testid="save-search-minage" /></div>
            <div><Label className="text-xs text-zinc-600">Max Age</Label><Input type="number" value={filters.max_age} onChange={(e) => setFilters({...filters, max_age: e.target.value})} className="border-[#E6E6E6] mt-1" data-testid="save-search-maxage" /></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="border-[#E6E6E6]">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#074572] hover:bg-[#053a60] text-white" data-testid="save-search-confirm">{saving ? "Saving..." : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============ EXPORT CSV ============ */
function ExportSection({ isPremium }) {
  const [athletes, setAthletes] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isPremium) return;
    (async () => {
      try {
        const { data } = await api.get("/favorites");
        setAthletes(data.athletes || []);
      } catch { /* ignore */ }
    })();
  }, [isPremium]);

  if (!isPremium) return <PremiumLock title="CSV Export is Premium" description="Export athlete data (bio + ergometrics) for offline analysis or to share with your scouting team." />;

  const toggle = (id) => { const next = new Set(selected); if (next.has(id)) next.delete(id); else next.add(id); setSelected(next); };
  const selectAll = () => setSelected(new Set(athletes.map(a => a._id)));
  const clearAll = () => setSelected(new Set());

  const download = async () => {
    setDownloading(true);
    try {
      const ids = [...selected].join(",");
      const res = await api.get(`/scout/export${ids ? `?ids=${ids}` : ""}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sportsunity-athletes-${new Date().toISOString().slice(0, 10)}.csv`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Exported ${selected.size || "all"} athletes`);
    } catch (err) { toast.error(err.response?.data?.detail || "Export failed"); }
    finally { setDownloading(false); }
  };

  return (
    <div data-testid="export-section">
      <div className="bg-white border border-[#E6E6E6] rounded-lg p-4 mb-4">
        <p className="text-xs text-zinc-600 mb-3">Export your shortlist (or selected athletes) as CSV — includes bio, position, club, ergometrics, and verification status.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={selectAll} variant="outline" className="border-[#E6E6E6] text-xs h-8" data-testid="export-select-all">Select All</Button>
          <Button onClick={clearAll} variant="outline" className="border-[#E6E6E6] text-xs h-8" data-testid="export-clear">Clear</Button>
          <div className="flex-1" />
          <Button onClick={download} disabled={downloading} className="bg-[#074572] hover:bg-[#053a60] text-white gap-1.5 text-xs h-8" data-testid="export-download-btn">
            <Download size={12} /> {downloading ? "Generating..." : `Download CSV (${selected.size || "all"})`}
          </Button>
        </div>
      </div>

      {athletes.length === 0 ? (
        <p className="text-center py-12 text-zinc-400 text-sm">Add athletes to your shortlist first to enable selection. (Empty selection exports all.)</p>
      ) : (
        <div className="space-y-1.5">
          {athletes.map(a => (
            <label key={a._id} className="flex items-center gap-3 px-3 py-2 bg-white border border-[#E6E6E6] rounded-md cursor-pointer hover:border-[#074572]/30">
              <input type="checkbox" checked={selected.has(a._id)} onChange={() => toggle(a._id)} className="w-4 h-4 accent-[#074572]" data-testid={`export-select-${a._id}`} />
              <span className="text-sm text-zinc-900">{a.name}</span>
              <span className="text-xs text-zinc-400">{a.position} · {a.club || "Free Agent"}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ GRAPHS ============ */
const GRAPH_COLORS = ["#F85037", "#074572", "#10B981", "#F59E0B", "#8B5CF6", "#06B6D4", "#EC4899", "#0EA5E9"];

const ERGO_DEFAULTS = {
  sprint_speed: { min: 20, max: 38 },
  vo2_max: { min: 35, max: 75 },
  vertical_jump: { min: 30, max: 90 },
  agility_time: { min: 3.5, max: 6.5 },
  bench_press: { min: 30, max: 150 },
  endurance_score: { min: 0, max: 100 },
};

function GraphsSection({ isPremium }) {
  const navigate = useNavigate();
  const [shortlist, setShortlist] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [peer, setPeer] = useState(null);

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/favorites");
        const athletes = data.athletes || [];
        setShortlist(athletes);
        // Auto-select first 4 with any ergo data
        const withData = athletes.filter(a => ERGO_FIELDS.some(f => a[f.key] != null));
        setSelectedIds(withData.slice(0, 4).map(a => a._id));
      } catch { toast.error("Failed to load shortlist"); }
      finally { setLoading(false); }
    })();
  }, [isPremium]);

  // Fetch peer averages based on most common position in selection
  useEffect(() => {
    if (selectedIds.length === 0) { setPeer(null); return; }
    const positions = selectedIds.map(id => shortlist.find(a => a._id === id)?.position).filter(Boolean);
    const counts = {};
    positions.forEach(p => { counts[p] = (counts[p] || 0) + 1; });
    const topPos = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
    let active = true;
    (async () => {
      try {
        const url = topPos ? `/scout/position-averages?position=${encodeURIComponent(topPos)}` : "/scout/position-averages";
        const { data } = await api.get(url);
        if (active) setPeer(data);
      } catch (e) {
        // No-op
      }
    })();
    return () => { active = false; };
  }, [selectedIds, shortlist]);

  if (!isPremium) return <PremiumLock title="Graphs are Premium" description="Visualise your shortlist with radar overlays and per-metric bar charts. Compare athletes against position averages in one glance." />;

  if (loading) return <p className="text-center py-12 text-zinc-400 text-sm">Loading graphs...</p>;

  if (shortlist.length === 0) {
    return (
      <div className="text-center py-16" data-testid="graphs-empty">
        <BarChart3 size={48} className="mx-auto text-zinc-200 mb-4" />
        <p className="text-zinc-500">No athletes in your shortlist yet</p>
        <p className="text-sm text-zinc-400 mt-1">Add athletes from Scout Navigator to start graphing them.</p>
        <Link to="/scout"><Button className="mt-4 bg-[#074572] hover:bg-[#053a60] text-white">Browse Scout Navigator</Button></Link>
      </div>
    );
  }

  const selectedAthletes = shortlist.filter(a => selectedIds.includes(a._id));
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(i => i !== id);
      if (prev.length >= 8) { toast.error("Maximum 8 athletes per graph"); return prev; }
      return [...prev, id];
    });
  };

  // Build axes and scale for radar (use peer min/max + buffer; fallback to defaults)
  const radarAxes = ERGO_FIELDS.map(f => ({ key: f.key, label: f.label, lowerBetter: f.key === "agility_time" }));
  const radarScale = {};
  ERGO_FIELDS.forEach(f => {
    const peerStat = peer?.stats?.[f.key];
    if (peerStat && peerStat.n > 1) {
      const spread = (peerStat.max - peerStat.min) || (peerStat.avg * 0.2) || 1;
      radarScale[f.key] = { min: Math.max(0, peerStat.min - spread * 0.2), max: peerStat.max + spread * 0.2 };
    } else {
      radarScale[f.key] = ERGO_DEFAULTS[f.key];
    }
  });

  const datasets = selectedAthletes.map((a, idx) => ({
    label: a.name?.split(" ")[0] || `A${idx}`,
    color: GRAPH_COLORS[idx % GRAPH_COLORS.length],
    values: Object.fromEntries(ERGO_FIELDS.map(f => [f.key, a[f.key]])),
  }));

  const peerSampleSize = peer?.sample_size || 0;
  const reference = peerSampleSize > 1 ? {
    label: `Peer avg (n=${peerSampleSize})`,
    color: "#94a3b8",
    values: Object.fromEntries(ERGO_FIELDS.map(f => [f.key, peer?.stats?.[f.key]?.avg])),
  } : null;

  return (
    <div data-testid="graphs-section">
      {/* Athlete selector */}
      <div className="bg-white border border-[#E6E6E6] rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <p className="text-xs text-zinc-500">{selectedIds.length}/{Math.min(8, shortlist.length)} athletes selected · max 8</p>
          <Button onClick={() => navigate("/dashboard?tab=shortlist")} variant="outline" className="border-[#E6E6E6] text-xs h-7 gap-1.5" data-testid="graphs-edit-shortlist">
            <Heart size={11} /> Manage Shortlist
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {shortlist.map((a, idx) => {
            const isSel = selectedIds.includes(a._id);
            const color = GRAPH_COLORS[selectedIds.indexOf(a._id) % GRAPH_COLORS.length];
            return (
              <button
                key={a._id}
                onClick={() => toggleSelect(a._id)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${isSel ? "text-white border-transparent" : "bg-white text-zinc-600 border-[#E6E6E6] hover:border-[#074572]"}`}
                style={isSel ? { backgroundColor: color } : {}}
                data-testid={`graph-toggle-${a._id}`}
              >
                {isSel && <span className="w-1.5 h-1.5 bg-white rounded-full" />}
                {a.name}
              </button>
            );
          })}
        </div>
      </div>

      {selectedAthletes.length === 0 ? (
        <p className="text-center py-12 text-zinc-400 text-sm">Select at least 1 athlete to visualise</p>
      ) : (
        <>
          {/* Radar Chart Card */}
          <div className="bg-white border border-[#E6E6E6] rounded-lg p-5 mb-4" data-testid="graph-radar-card">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={16} className="text-[#074572]" />
              <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Performance Radar</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-4">Side-by-side ergometric profile across 6 axes{peer?.position && peer.position !== 'all' ? ` (scaled to ${peer.position} averages)` : ''}.</p>
            <div className="flex justify-center bg-zinc-50 rounded-md py-4">
              <RadarChart
                size={340}
                axes={radarAxes}
                datasets={datasets}
                reference={reference}
                scale={radarScale}
                labelColor="#3f3f46"
                axisColor="rgba(0,0,0,0.08)"
                bgColor="transparent"
              />
            </div>
          </div>

          {/* Per-metric Bar Charts */}
          <div className="bg-white border border-[#E6E6E6] rounded-lg p-5" data-testid="graph-bars-card">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={16} className="text-[#074572]" />
              <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Per-Metric Comparison</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-4">Dashed line shows peer/position average. Red dot indicates the leader on each metric.</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-5 gap-y-4">
              {ERGO_FIELDS.map(f => {
                const athletesForBar = selectedAthletes.map((a, idx) => ({
                  id: a._id,
                  name: a.name?.split(" ")[0] || "A",
                  color: GRAPH_COLORS[idx % GRAPH_COLORS.length],
                  value: a[f.key],
                }));
                const peerAvg = peer?.stats?.[f.key]?.avg;
                return (
                  <div key={f.key} data-testid={`graph-bar-${f.key}`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className="text-xs font-semibold text-zinc-700">{f.label}</span>
                      <span className="text-[10px] text-zinc-400">{f.unit}</span>
                      {f.key === "agility_time" && <span className="text-[9px] text-zinc-400 italic">lower is better</span>}
                    </div>
                    <BarChart
                      metric={{ key: f.key, label: f.label, unit: f.unit, lowerBetter: f.key === "agility_time" }}
                      athletes={athletesForBar}
                      reference={peerAvg != null ? { label: "Avg", value: peerAvg, color: "#94a3b8" } : null}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ============ PREMIUM LOCK ============ */
function PremiumLock({ title, description }) {
  return (
    <div className="text-center py-16 border border-dashed border-amber-200 bg-amber-50/40 rounded-lg" data-testid="premium-lock">
      <Crown size={48} className="mx-auto text-amber-400 mb-4" />
      <h3 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>{title}</h3>
      <p className="text-sm text-zinc-600 mt-1 max-w-md mx-auto">{description}</p>
      <Link to="/premium">
        <Button className="mt-5 bg-[#F85037] hover:bg-[#e04530] text-white gap-1.5" data-testid="lock-upgrade-btn">
          <Crown size={14} /> Upgrade to Premium
        </Button>
      </Link>
    </div>
  );
}
